import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { coursesApi, type AccessCategory } from '@/api/courses';
import { Button } from '@/components/ui/Button';
import { Card, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Pill } from '@/components/ui/Pill';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import {
  ACCESS_CATEGORIES,
  classificationBody,
  isValidSubtype,
  knownSubtypes,
  normalizeSubtypeInput,
  subtypeChanges,
} from '@/lib/marathonClassification';
import {
  CONFLICT_LEVELS,
  classificationErrorText,
  joinConflict,
  outsideMirror,
} from '@/lib/marathonClassificationErrors';
import { MirrorAdoptDialog, MirrorAdoptJournal } from './MirrorAdoptDialog';

/**
 * Подтип курса и категория доступа (договор `docs/CONTRACTS/mirror-v2.md` §1–2).
 *
 * ⚠️ ПОЛЕ ПОДТИПА — ТОЛЬКО ПРИ `subtype_editable`. Признак приходит с сервера и
 * значит «спросивший есть в `courses.subtype_owners`». Остальным админам сервер
 * ответит 403 `subtype_owner_only`, поэтому им показывается то же, что и
 * раньше: подтип строкой, без возможности его тронуть.
 *
 * ⚠️ ПОДТИП МЕНЯЕТСЯ ЧЕРЕЗ ОКНО ПОДТВЕРЖДЕНИЯ (решение владельца 20.09.2026).
 * Курс с подтипом попадает в зеркало: его уроки разъезжаются по живым курсам
 * потока, а чужие приезжают к нему. Сохранение одной категории доступа окна не
 * требует — она ничего никуда не двигает.
 *
 * ⚠️ `confirm_title` БЕРЁТСЯ С СЕРВЕРА, А НЕ НАБИРАЕТСЯ. Сервер сверяет его с
 * названием буква в букву, а названия на проде кривые — двойной пробел,
 * обратный апостроф. Пока название не пришло, менять подтип нельзя: пустое
 * подтверждение вернуло бы 400 и выглядело бы поломкой экрана.
 */
export function ClassificationCard({ marathonId }: { marathonId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ask = useConfirm();

  const settings = useQuery({
    queryKey: ['admin', 'marathon', marathonId, 'settings'],
    queryFn: () => coursesApi.getMarathonSettings(marathonId),
  });

  // Тот же ключ, что у страницы курса, — название придёт из кэша, лишнего
  // запроса не будет.
  const marathon = useQuery({
    queryKey: ['admin', 'marathon', marathonId],
    queryFn: () => coursesApi.getMarathon(marathonId),
  });

  // Тот же ключ, что у списка «дополнительных курсов» на этой же вкладке.
  const all = useQuery({
    queryKey: ['admin', 'marathons', 'for-extras'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100 }),
  });

  const saved = useMemo(
    () => ({
      subtype: settings.data?.subtype ?? '',
      category: settings.data?.access_category ?? null,
    }),
    [settings.data],
  );

  const [subtype, setSubtype] = useState('');
  const [category, setCategory] = useState<AccessCategory | null>(null);
  const [adoptOpen, setAdoptOpen] = useState(false);

  // Черновик идёт за сервером: после сохранения и перечитывания он равен ответу.
  useEffect(() => {
    setSubtype(saved.subtype);
    setCategory(saved.category);
  }, [saved]);

  const canEditSubtype = settings.data?.subtype_editable === true;
  const title = marathon.data?.title ?? '';
  const draft = { subtype, category };
  const body = classificationBody(saved, draft, title, canEditSubtype);
  const typed = normalizeSubtypeInput(subtype);
  const subtypeBad = typed !== '' && !isValidSubtype(typed);
  // Смена подтипа без названия ушла бы с пустым `confirm_title` — это 400.
  const titleMissing = canEditSubtype && subtypeChanges(saved, draft) && title === '';

  const save = useMutation({
    mutationFn: () => coursesApi.setMarathonClassification(marathonId, body ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId, 'settings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'marathon', marathonId] });
      qc.invalidateQueries({ queryKey: ['admin', 'marathons', 'for-extras'] });
    },
  });

  const onSave = async () => {
    if (!body) return;
    if ('subtype' in body) {
      const next = body.subtype;
      const message = next
        ? t(saved.subtype ? 'marathonClassification.confirmChange' : 'marathonClassification.confirmSet', {
            title,
            subtype: next,
            was: saved.subtype,
          })
        : t('marathonClassification.confirmClear', { title, was: saved.subtype });
      const ok = await ask({
        title: t('marathonClassification.confirmTitle'),
        message,
        confirmLabel: t('marathonClassification.confirmButton'),
        danger: true,
      });
      if (!ok) return;
    }
    save.mutate();
  };

  const conflict = joinConflict(save.error);
  const outside = outsideMirror(save.error);
  // «Принять содержимое потока» (договор `mirror-adopt.md`): у курса в потоке — всегда, у курса вне потока —
  // рядом с отказом входа, в тот поток, куда его не пустили. Предпросмотр доступен любому админу.
  const adoptSubtype = saved.subtype || conflict?.subtype || outside?.subtype || '';
  const OUTSIDE_SHOWN = 10;
  const levelList = (counts: Partial<Record<(typeof CONFLICT_LEVELS)[number], number>>) =>
    CONFLICT_LEVELS.filter((l) => counts[l])
      .map((l) => `${t(`marathonClassification.level_${l}`)}: ${counts[l]}`)
      .join(', ');

  if (settings.isLoading) return null;

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-3">
        <CardTitle>{t('marathonClassification.title')}</CardTitle>
        {saved.subtype ? (
          <Pill variant="neutral">{saved.subtype}</Pill>
        ) : (
          <Pill variant="neutral">{t('marathonClassification.noSubtype')}</Pill>
        )}
      </div>
      <p className="mb-4 text-[13px] text-on-surface-variant">{t('marathonClassification.hint')}</p>

      {canEditSubtype ? (
        <div className="mb-4">
          <Input
            label={t('marathonClassification.subtypeLabel')}
            value={subtype}
            list="marathon-subtypes"
            placeholder={t('marathonClassification.subtypePlaceholder')}
            hint={t('marathonClassification.subtypeFormat')}
            error={subtypeBad ? t('marathonClassification.subtypeInvalid') : undefined}
            onChange={(e) => setSubtype(e.target.value)}
          />
          <datalist id="marathon-subtypes">
            {knownSubtypes(all.data?.items ?? []).map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
      ) : (
        <p className="mb-4 text-[12px] text-on-surface-variant">
          {t('marathonClassification.subtypeOwnerOnlyHint')}
        </p>
      )}

      <p className="mb-2 text-[13px] font-semibold text-on-surface">
        {t('marathonClassification.categoryLabel')}
      </p>
      <div className="grid gap-2">
        {([null, ...ACCESS_CATEGORIES] as (AccessCategory | null)[]).map((c) => (
          <label
            key={c ?? 'none'}
            className="flex cursor-pointer items-center gap-2.5 rounded-button bg-surface-container-low px-3.5 py-2.5"
          >
            <input
              type="radio"
              name="access-category"
              checked={category === c}
              onChange={() => setCategory(c)}
              className="h-4 w-4 accent-primary"
            />
            <span className="text-[13.5px] text-on-surface">
              {c ? t(`marathonClassification.category_${c}`) : t('marathonClassification.categoryNone')}
            </span>
          </label>
        ))}
      </div>

      <Button
        className="mt-4"
        onClick={onSave}
        disabled={!body || subtypeBad || titleMissing || save.isPending}
      >
        {save.isPending ? t('common.saving') : t('common.save')}
      </Button>

      {titleMissing && (
        <p className="mt-2 text-[12px] text-on-surface-variant">
          {t('marathonClassification.titleMissing')}
        </p>
      )}

      {conflict ? (
        <div className="mt-3 rounded-card bg-surface-container-lowest px-3 py-2 text-sm text-error">
          <p className="font-semibold">
            {t('marathonClassification.conflictTitle', { subtype: conflict.subtype })}
          </p>
          {levelList(conflict.wouldAdd) && (
            <p className="mt-1">
              {t('marathonClassification.conflictAdd', { list: levelList(conflict.wouldAdd) })}
            </p>
          )}
          {levelList(conflict.wouldOverwrite) && (
            <p className="mt-1">
              {t('marathonClassification.conflictOverwrite', {
                list: levelList(conflict.wouldOverwrite),
              })}
            </p>
          )}
          <p className="mt-1">{t('marathonClassification.conflictWhatToDo')}</p>
        </div>
      ) : outside ? (
        <div className="mt-3 rounded-card bg-surface-container-lowest px-3 py-2 text-sm text-error">
          <p className="font-semibold">
            {t('marathonClassification.conflictTitle', { subtype: outside.subtype })}
          </p>
          <p className="mt-1">
            {t('marathonClassification.outsideMirrorLessons', { n: outside.lessonsTotal })}
          </p>
          {outside.lessons.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {outside.lessons.slice(0, OUTSIDE_SHOWN).map((l) => (
                <li key={l.id}>{l.title}</li>
              ))}
            </ul>
          )}
          {outside.lessonsTotal > Math.min(outside.lessons.length, OUTSIDE_SHOWN) && (
            <p className="mt-1">
              {t('marathonClassification.outsideMirrorMore', {
                n: outside.lessonsTotal - Math.min(outside.lessons.length, OUTSIDE_SHOWN),
              })}
            </p>
          )}
          <p className="mt-1">{t('marathonClassification.outsideMirrorWhatToDo')}</p>
        </div>
      ) : (
        save.isError && (
          <p className="mt-2 text-sm text-error">{classificationErrorText(save.error, t)}</p>
        )
      )}

      {save.isSuccess && !save.isError && !body && (
        <p className="mt-2 text-sm text-success">{t('marathonClassification.saved')}</p>
      )}

      {adoptSubtype && (
        <div className="mt-4">
          <Button variant="secondary" onClick={() => setAdoptOpen(true)}>
            {t('mirrorAdopt.open')}
          </Button>
          <p className="mt-1 text-[12px] text-on-surface-variant">{t('mirrorAdopt.openHint')}</p>
        </div>
      )}
      {adoptOpen && (
        <MirrorAdoptDialog
          open={adoptOpen}
          onClose={() => setAdoptOpen(false)}
          marathonId={marathonId}
          title={title}
          subtype={adoptSubtype}
          subtypeFixed={!!saved.subtype}
          subtypes={knownSubtypes(all.data?.items ?? [])}
          editable={canEditSubtype}
        />
      )}
      <MirrorAdoptJournal marathonId={marathonId} title={title} editable={canEditSubtype} />
    </Card>
  );
}
